import { defineField, defineType } from "sanity";

export const homePage = defineType({
  name: "homePage",
  title: "Home Page",
  type: "document",
  groups: [
    { name: "hero", title: "Hero", default: true },
    { name: "blurbs", title: "Blurbs" },
    { name: "gallery", title: "Gallery" },
    { name: "newArrivals", title: "New Arrivals" },
    { name: "seo", title: "SEO" }
  ],
  fields: [
    // ── Hero ───────────────────────────────────────────────────────────
    defineField({
      name: "heroTitle",
      title: "Hero title",
      type: "string",
      description: "Main headline on the home page.",
      validation: (Rule) =>
        Rule.required().max(80).error("Keep the hero title under 80 characters."),
      group: "hero"
    }),
    defineField({
      name: "heroSubtitle",
      title: "Hero subtitle",
      type: "text",
      rows: 3,
      description: "Support copy under the hero title.",
      validation: (Rule) => Rule.max(200).warning("Keep the hero subtitle under 200 characters."),
      group: "hero"
    }),
    defineField({
      name: "heroImage",
      title: "Hero image/video",
      type: "image",
      description: "Use an image for now. If you need video, add it below.",
      options: { hotspot: true },
      fields: [
        defineField({
          name: "alt",
          title: "Alt text",
          type: "string",
          description: "Describe the image for screen readers.",
          validation: (Rule) =>
            Rule.custom((value) => (value ? true : "Add alt text so the image is accessible.")).warning(
              "Add alt text so the image is accessible."
            )
        })
      ],
      group: "hero"
    }),
    defineField({
      name: "heroVideo",
      title: "Hero video (optional)",
      type: "file",
      description: "Upload a short looping MP4 if you want video in the hero.",
      options: { accept: "video/*" },
      group: "hero"
    }),
    defineField({
      name: "heroCtaPrimaryText",
      title: "Hero CTA primary text",
      type: "string",
      description: "Text for the primary hero button (e.g. 'Inside the Vault').",
      group: "hero"
    }),
    defineField({
      name: "heroCtaPrimaryLink",
      title: "Hero CTA primary link",
      type: "string",
      description: "Link for the primary hero button (e.g. /browse).",
      group: "hero"
    }),
    defineField({
      name: "heroCtaSecondaryText",
      title: "Hero CTA secondary text",
      type: "string",
      description: "Text for the secondary hero button (e.g. 'Learn our story').",
      group: "hero"
    }),
    defineField({
      name: "heroCtaSecondaryLink",
      title: "Hero CTA secondary link",
      type: "string",
      description: "Link for the secondary hero button (e.g. /about).",
      group: "hero"
    }),
    defineField({
      name: "heroCtaTertiaryText",
      title: "Hero CTA tertiary text",
      type: "string",
      description: "Text for the third hero button (e.g. 'Swipe to discover'). Leave empty to hide.",
      group: "hero"
    }),
    defineField({
      name: "heroCtaTertiaryLink",
      title: "Hero CTA tertiary link",
      type: "string",
      description: "Link for the tertiary hero button (e.g. /browse/swipe).",
      group: "hero"
    }),
    defineField({
      name: "footerText",
      title: "Footer text",
      type: "text",
      rows: 2,
      description: "Short line of copy shown in the footer.",
      group: "hero"
    }),

    // ── Blurbs ─────────────────────────────────────────────────────────
    defineField({
      name: "firstBlurbImage",
      title: "First Blurb Image",
      type: "image",
      description: "Image displayed next to the first blurb section.",
      options: { hotspot: true },
      fields: [
        defineField({
          name: "alt",
          title: "Alt text",
          type: "string",
          description: "Describe the image for screen readers."
        })
      ],
      group: "blurbs"
    }),
    defineField({
      name: "firstBlurbTitle",
      title: "First Blurb Title",
      type: "string",
      description: "Small label above the first blurb.",
      group: "blurbs"
    }),
    defineField({
      name: "firstBlurbHeader",
      title: "First Blurb Header",
      type: "string",
      description: "Headline for the first blurb section.",
      group: "blurbs"
    }),
    defineField({
      name: "firstBlurbBody",
      title: "First Blurb Body",
      type: "text",
      rows: 4,
      description: "Paragraph copy for the first blurb section.",
      group: "blurbs"
    }),
    defineField({
      name: "secondBlurbImage",
      title: "Second Blurb Image",
      type: "image",
      description: "Image displayed next to the second blurb section.",
      options: { hotspot: true },
      fields: [
        defineField({
          name: "alt",
          title: "Alt text",
          type: "string",
          description: "Describe the image for screen readers."
        })
      ],
      group: "blurbs"
    }),
    defineField({
      name: "secondBlurbTitle",
      title: "Second Blurb Title",
      type: "string",
      description: "Small label above the second blurb.",
      group: "blurbs"
    }),
    defineField({
      name: "secondBlurbHeader",
      title: "Second Blurb Header",
      type: "string",
      description: "Headline for the second blurb section.",
      group: "blurbs"
    }),
    defineField({
      name: "secondBlurbBody",
      title: "Second Blurb Body",
      type: "text",
      rows: 4,
      description: "Paragraph copy for the second blurb section.",
      group: "blurbs"
    }),

    // ── Gallery ────────────────────────────────────────────────────────
    defineField({
      name: "galleryLabel",
      title: "Gallery label",
      type: "string",
      description: "Small label above the gallery heading (e.g. 'Featured gallery').",
      group: "gallery"
    }),
    defineField({
      name: "galleryHeading",
      title: "Gallery heading",
      type: "string",
      description: "Heading for the gallery section (e.g. 'Gallery highlights').",
      group: "gallery"
    }),
    defineField({
      name: "galleryCtaText",
      title: "Gallery CTA text",
      type: "string",
      description: "Link text for viewing all pieces (e.g. 'View all pieces').",
      group: "gallery"
    }),
    defineField({
      name: "gallery",
      title: "Gallery",
      type: "array",
      description: "Add up to 12 images for the featured gallery.",
      of: [
        defineField({
          name: "galleryImage",
          type: "image",
          title: "Gallery image",
          options: { hotspot: true },
          fields: [
            defineField({
              name: "alt",
              title: "Alt text",
              type: "string",
              description: "Describe the image for screen readers.",
              validation: (Rule) =>
                Rule.custom((value) => (value ? true : "Add alt text so the image is accessible.")).warning(
                  "Add alt text so the image is accessible."
                )
            })
          ]
        })
      ],
      validation: (Rule) => Rule.max(12).error("Gallery can have up to 12 images."),
      group: "gallery"
    }),

    // ── New Arrivals ───────────────────────────────────────────────────
    defineField({
      name: "newArrivalsHeroImage",
      title: "New arrivals hero image",
      type: "image",
      description: "Hero image shown at the top of the New Arrivals page.",
      options: { hotspot: true },
      fields: [
        defineField({
          name: "alt",
          title: "Alt text",
          type: "string",
          description: "Describe the image for screen readers."
        })
      ],
      group: "newArrivals"
    }),
    defineField({
      name: "newArrivalsLabel",
      title: "New arrivals label",
      type: "string",
      description: "Small label above the new arrivals heading (e.g. 'Just added').",
      group: "newArrivals"
    }),
    defineField({
      name: "newArrivalsHeading",
      title: "New arrivals heading",
      type: "string",
      description: "Heading for the new arrivals section (e.g. 'New Arrivals').",
      group: "newArrivals"
    }),
    defineField({
      name: "newArrivalsCtaText",
      title: "New arrivals CTA text",
      type: "string",
      description: "Link text for viewing all new arrivals (e.g. 'View all new arrivals').",
      group: "newArrivals"
    }),

    // ── SEO ────────────────────────────────────────────────────────────
    defineField({
      name: "metaTitle",
      title: "Meta title",
      type: "string",
      description: "Page title for search engines. Overrides the site-wide SEO title for this page.",
      group: "seo"
    }),
    defineField({
      name: "metaDescription",
      title: "Meta description",
      type: "text",
      rows: 3,
      description: "Short description for search engines. Overrides the site-wide SEO description for this page.",
      group: "seo"
    }),
    defineField({
      name: "ogImage",
      title: "Sharing image",
      type: "image",
      description: "Image shown when this page is shared on social media.",
      options: { hotspot: true },
      fields: [
        defineField({
          name: "alt",
          title: "Alt text",
          type: "string",
          description: "Describe the image for screen readers."
        })
      ],
      group: "seo"
    })
  ],
  preview: {
    prepare: () => ({
      title: "Home Page"
    })
  }
});
